import { GQL } from 'rewire-graphql';
import {
  IUdt,
  ISubscriptionRecord
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import gql from 'graphql-tag';
import udfsSubscriptionService from './UdfsSubscriptionService';
import EntitySubscriptionService, {
  EntitySubscriptionModel,
  SubscriptionCallbackFn,
} from './EntitySubscriptionService';
import { subscribe } from '../graphql/GraphQLClient';

const udtsSubscription = (returnSchema: string) => gql`
  subscription {
    udts {
      udt {
        ${returnSchema}
      }
      operation
    }
  }
`;

export function subscribeToUdts(returnSchema: string, callback: any): any {
  return subscribe(udtsSubscription(returnSchema) as GQL, undefined, callback, 'udts');
}

export type UdtSort = (u1: IUdt, u2: IUdt) => number;

export function defaultUdtSort(u1: IUdt, u2: IUdt) {
  return u1.displayName.localeCompare(u2.displayName);
}

export const defaultUdtSchema = `
  id
  locationId
  name
  displayName
`;

export class UdtsSubscriptionModel extends EntitySubscriptionModel<IUdt> {
  constructor(options?: { returnSchema?: string, externalEntitiesContainer?: IUdt[], subscriptionCallback?: SubscriptionCallbackFn<IUdt> }) {
    super({ returnSchema: options?.returnSchema || defaultUdtSchema, filter: {}, sort: defaultUdtSort, externalEntitiesContainer: options?.externalEntitiesContainer, subscriptionCallback: options?.subscriptionCallback });
  }

  set udts(value: IUdt[]) {
    this.entities = value;
  }

  get udts(): IUdt[] {
    return this.entities;
  }

  get udtCount(): number {
    return this.entityCount;
  }

  getUdtByName(name: string): IUdt | undefined {
    return this.udts.find(u => u.name === name);
  }

  getUdtsByIds(ids: string[]): IUdt[] {
    return this.udts.filter(u => ids.some(id => id === u.id));
  }

  protected async _getEntities(returnSchema: string): Promise<IUdt[]> {
    const result = await DataQueries.getUserDefinedTypes(returnSchema);
    return (result ?? []) as IUdt[];
  }

  protected subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any {
    return subscribeToUdts(returnSchema, callback);
  }

  protected mergeAccessor(udtRecord: ISubscriptionRecord<IUdt>): IUdt {
    return udtRecord['udt'] as IUdt;
  }

  protected getSubscriptionCallback() {
    return async (udtRecord: ISubscriptionRecord<IUdt>) => {
      await this.merge(udtRecord);
      this.updateUdfsData(udtRecord);
      this._subscriptionCallback?.(udtRecord);
    };
  }

  protected updateUdfsData(udtRecord: ISubscriptionRecord<IUdt>) {
    const udt = udtRecord['udt'] as IUdt;
    if (udtRecord.operation === 'deleted') {
      delete udfsSubscriptionService.subscriptionModel.udfDocumentTypes[udt.name];
      delete udfsSubscriptionService.subscriptionModel.udfsByDocumentTypeMap[udt.name];
    } else {
      udfsSubscriptionService.subscriptionModel.udfDocumentTypes[udt.name] = udt.displayName;
      if (!udfsSubscriptionService.subscriptionModel.udfsByDocumentTypeMap[udt.name]) {
        udfsSubscriptionService.subscriptionModel.udfsByDocumentTypeMap[udt.name] = [];
      }
    }
  }
}

class UdtsSubscriptionService extends EntitySubscriptionService<IUdt, UdtsSubscriptionModel> {
  constructor() {
    super();
    this.createSubscriptionModel();
  }

  get subscriptionModel(): UdtsSubscriptionModel {
    return this.subscriptionModels[0] as UdtsSubscriptionModel;
  }

  createSubscriptionModel(options?: { returnSchema?: string, externalEntitiesContainer?: IUdt[], subscriptionCallback?: SubscriptionCallbackFn<IUdt> }): UdtsSubscriptionModel {
    if (!this.subscriptionModels.length) {
      super.createSubscriptionModel(options);
    }

    return this.subscriptionModel;
  }

  protected _createSubscriptionModel(options?: { returnSchema?: string, externalEntitiesContainer?: IUdt[], subscriptionCallback?: SubscriptionCallbackFn<IUdt> }): UdtsSubscriptionModel {
    return new UdtsSubscriptionModel(options);
  }
}

const udtsSubscriptionService = new UdtsSubscriptionService();
export default udtsSubscriptionService;
